import TrashIcon from "~icons/fa6-regular/trash-can";
import CopyIcon from "~icons/fa6-regular/copy";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ReactElement } from "react";
import { ScrollSyncPane } from "react-scroll-sync";
import ActionsColumn from "./ActionsColumn";
import { useDeleteOperation } from "@/lib/queries/hooks";
import { operationLeftPanelWidth, quantityColumnWidth } from "../constants";
import useOperationContext from "./useOperationContext";
import $api from "@/lib/queries/openapi";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";
import useOperationListContext from "./useOperationListContext";

export default function OperationLayout({
  id,
  left,
  right,
}: {
  id: string;
  left: ReactElement;
  right: ReactElement;
}) {
  const { allowActions } = useOperationContext();
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const { deleteOperation } = useDeleteOperation();
  const { teamSlug, partId } = useParams();
  const queryClient = useQueryClient();
  const { mutate: duplicate } = $api.useMutation(
    "post",
    "/api/teams/{team_slug}/operations/{id}/duplicate",
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            "get",
            "/api/teams/{team_slug}/requested_parts/{id}",
            {
              params: {
                path: {
                  team_slug: teamSlug,
                  id: partId,
                },
              },
            },
          ],
        });
      },
    },
  );

  return (
    <div
      className="w-full flex rounded-tl-sm rounded-bl-sm"
      ref={setNodeRef}
      style={style}
    >
      <div
        className="bg-white border-r border-slate-100"
        style={{
          minWidth: operationLeftPanelWidth,
          maxWidth: operationLeftPanelWidth,
        }}
      >
        {left}
      </div>
      <ScrollSyncPane>
        <div className="overflow-x-auto scrollbar-none flex flex-col justify-end bg-white">
          <RightColumnsPlaceholder />
          {right}
        </div>
      </ScrollSyncPane>

      <ActionsColumn>
        {allowActions && (
          <>
            <TrashIcon
              className="cursor-pointer size-3 text-slate-400 hover:text-slate-600"
              onClick={() => deleteOperation({ operationId: id })}
            />
            <svg
              viewBox="0 0 20 20"
              className="cursor-grab text-slate-400 hover:text-slate-600 size-3"
              fill="currentColor"
              {...attributes}
              {...listeners}
              tabIndex={-1}
            >
              <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"></path>
            </svg>
            <CopyIcon
              className="cursor-pointer size-3 text-slate-400 hover:text-slate-600"
              onClick={() =>
                duplicate({
                  params: {
                    path: { team_slug: teamSlug!, id },
                  },
                })
              }
            />
          </>
        )}
      </ActionsColumn>
    </div>
  );
}

function RightColumnsPlaceholder() {
  const { quantities } = useOperationListContext();

  return (
    <div className="grow flex divide-x divide-slate-100">
      {quantities.map((q) => (
        <div
          key={q}
          style={{
            maxWidth: quantityColumnWidth,
            minWidth: quantityColumnWidth,
          }}
        />
      ))}
    </div>
  );
}
